/**
 * iter77bh: Promise-based confirm dialog (singleton) to replace native window.confirm().
 *
 * Usage:
 *   import { confirmDialog } from '@/components/ConfirmDialog';
 *   const ok = await confirmDialog({
 *     title: 'Excluir nota?',
 *     message: 'Esta acao nao pode ser desfeita.',
 *     confirmLabel: 'Excluir',
 *     cancelLabel: 'Cancelar',
 *     danger: true,
 *   });
 *   if (!ok) return;
 *
 * Mount <ConfirmDialogHost /> ONCE at the app root.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

const EVENT_NAME = '__confirm_dialog_request__';

export function confirmDialog(options = {}) {
  return new Promise((resolve) => {
    const detail = { ...options, _resolve: resolve };
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
  });
}

const VARIANT_CONFIG = {
  danger: { Icon: AlertTriangle, accent: 'text-rose-600', bg: 'bg-rose-50', confirmBtn: 'bg-rose-600 hover:bg-rose-700' },
  warn:   { Icon: AlertTriangle, accent: 'text-amber-600', bg: 'bg-amber-50', confirmBtn: 'bg-amber-600 hover:bg-amber-700' },
  info:   { Icon: Info,          accent: 'text-sky-600',   bg: 'bg-sky-50',   confirmBtn: 'bg-sky-600 hover:bg-sky-700' },
  success:{ Icon: CheckCircle2,  accent: 'text-emerald-600', bg: 'bg-emerald-50', confirmBtn: 'bg-emerald-600 hover:bg-emerald-700' },
};

export const ConfirmDialogHost = () => {
  const [stack, setStack] = useState([]); // queue of pending dialogs
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setStack((s) => [...s, e.detail]);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  if (stack.length === 0) return null;
  const current = stack[0];
  const {
    title = 'Confirmar acao',
    message = '',
    description = '',
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    danger = false,
    variant = (danger ? 'danger' : 'info'),
    hideCancel = false,
    defaultCancel = false,  // iter156fb: foco/Enter no botao cancelar (ex.: 'Nao' pre-selecionado)
    _resolve,
  } = current;
  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
  const Icon = cfg.Icon;

  const close = (value) => {
    try { _resolve && _resolve(!!value); } catch { /* noop */ }
    setStack((s) => s.slice(1));
  };

  const onKey = (e) => {
    if (e.key === 'Escape') close(false);
    if (e.key === 'Enter') close(!defaultCancel);
  };

  const node = (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={onKey}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={(e) => { if (e.target === e.currentTarget) close(false); }}
      data-testid="confirm-dialog-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 animate-in zoom-in-95 fade-in duration-200"
        data-testid="confirm-dialog"
      >
        <div className="flex items-start gap-4 p-6">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${cfg.bg}`}>
            <Icon size={24} className={cfg.accent} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-slate-900 leading-tight" data-testid="confirm-dialog-title">{title}</h3>
            {message && (
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed" data-testid="confirm-dialog-message">{message}</p>
            )}
            {description && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed whitespace-pre-line" data-testid="confirm-dialog-description">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => close(false)}
            className="flex-shrink-0 p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            data-testid="confirm-dialog-close"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100">
          {!hideCancel && (
            <button
              type="button"
              onClick={() => close(false)}
              autoFocus={defaultCancel}
              className={`px-4 py-2 rounded-lg border text-sm font-bold transition-colors ${defaultCancel ? 'border-slate-800 bg-slate-800 text-white hover:bg-slate-900 shadow-md ring-2 ring-slate-300' : 'border-slate-300 bg-white hover:bg-slate-50 text-slate-700'}`}
              data-testid="confirm-dialog-cancel"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => close(true)}
            autoFocus={!defaultCancel}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${defaultCancel ? 'border border-slate-300 bg-white hover:bg-slate-50 text-slate-700' : `text-white shadow-md ${cfg.confirmBtn}`}`}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default ConfirmDialogHost;
